import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPhoneAlt, FaEnvelope } from 'react-icons/fa';

// Logo asset
import Logo from '../assets/logo.png';

// Sub-sections for Homepage2
import PremiumInfluencer from '../Components/Influencer/PremiumInfluencer';
import ServicesSection from '../Components/Homepage2/ServicesSection';
import WhyChooseUsSection from '../Components/Homepage2/WhyChooseUsSection';
import EditorialSection from '../Components/Homepage2/EditorialSection';
import HowSection from '../Components/Homepage2/HowSection';
import IndustryExpertiseSection from '../Components/Homepage2/IndustryExpertiseSection';
import TestimonialsSection from '../Components/Homepage2/TestimonialsSection';
import FAQSection from '../Components/Homepage2/FAQSection';
import FooterSection from '../Components/Homepage2/FooterSection';

// SEO & Conversion Widgets
import PageSEO from '../Components/SEO/PageSEO';
import OrganizationSchema from '../Components/SEO/OrganizationSchema';
import PersistentCTA from '../Components/CRO/PersistentCTA';
import StickyMobileCTA from '../Components/CRO/StickyMobileCTA';

// Rotating hero phrases
const HERO_PHRASES = [
  { line1: 'grow', line2: 'exponentially' },
  { line1: 'scale', line2: 'globally'      },
  { line1: 'convert', line2: 'audiences'   },
  { line1: 'build', line2: 'legacy'        },
];

const AnimatedHeroHeadline = React.memo(() => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % HERO_PHRASES.length), 2800);
    return () => clearInterval(t);
  }, []);

  const phrase = HERO_PHRASES[idx];

  return (
    <div className="select-none w-full mb-8">
      {/* Static lines */}
      <motion.p
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="text-[13px] sm:text-sm uppercase tracking-[0.25em] text-[#8A9A8A] font-semibold mb-4"
      >
        ✦ Digital Marketing Agency · Bangalore ✦
      </motion.p>

      <h1 className="text-5xl md:text-6xl lg:text-[72px] font-normal tracking-tight text-black leading-[1.05]">
        {/* Static "We help brands" */}
        <motion.span
          className="block"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        >
          We help brands
        </motion.span>

        {/* Animated cycling
            Mobile  : each word on its own line (block) → 2-line slot
            Desktop : both words inline on one line (lg:inline) → 1-line slot
            Extra height (0.25em) prevents descenders (g, y, p) being clipped */}
        <span className="block overflow-hidden h-[2.7em] lg:h-[1.3em]">
          <AnimatePresence mode="wait">
            <motion.span
              key={idx}
              className="block font-bold"
              style={{
                background: 'linear-gradient(90deg, #FF7332, #FF1E90, #7000FF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
              initial={{ y: '105%', opacity: 0 }}
              animate={{ y: '0%',   opacity: 1 }}
              exit={{    y: '-105%', opacity: 0 }}
              transition={{ duration: 0.52, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="block lg:inline">{phrase.line1}</span>
              {' '}
              <span className="block lg:inline">{phrase.line2}</span>
            </motion.span>
          </AnimatePresence>
        </span>
      </h1>
    </div>
  );
});

const Homepage2 = () => {
  const videoRef      = useRef(null);
  const prevXRef      = useRef(null);
  const loopRef       = useRef(false);
  const targetTimeRef = useRef(0);   // where mouse wants to be
  const displayedRef  = useRef(0);   // lazily follows target (lerp buffer)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedServices, setServices] = useState([]);
  const [scrolled, setScrolled] = useState(false);

  // Heuristic: < 4 GB RAM or < 4 logical cores → treat as low-end GPU
  const isLowEnd = useRef(
    (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory < 4) ||
    (typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency < 4)
  );

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 40); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const availableServices = [
    "Digital Marketing",
    "SaaS Development",
    "Website Development",
    "AI Automation",
    "Campaign Strategy",
    "Brand Identity",
    "Other",
  ];

  // Video hook — mouse-direction scrub on laptop/desktop, autoplay loop on mobile
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const isMobile = window.innerWidth < 1024;

    // Mobile only: autoplay loop
    if (isMobile) {
      video.loop = true;
      video.play().catch(() => {});
      return;
    }

    // Desktop / laptop (≥ 1024px): event-driven seek chain
    video.loop = false;
    video.pause();

    const handleMetadata = () => {
      video.pause();
      targetTimeRef.current = video.currentTime;
      displayedRef.current  = video.currentTime;
    };
    video.addEventListener('loadedmetadata', handleMetadata);
    if (video.readyState >= 1) {
      video.pause();
      targetTimeRef.current = video.currentTime;
      displayedRef.current  = video.currentTime;
    }

    // ── Event-driven seek chain — the correct architecture ─────────────
    // Problem with rAF loops: they write video.currentTime at 60fps but
    // the GPU decoder takes 20-50ms per seek on integrated graphics.
    // Result: decoder queue builds up → seeks complete out of order → jitter.
    //
    // Solution: only send seek N+1 AFTER seeked event fires for seek N.
    // The GPU decoder runs at exactly its own comfortable speed.
    // No queue, no stalls, no jitter — on any hardware, any GPU.
    //
    // LERP = 0.14 gives smooth easing. Fast GPU → fast seeks → fluid 60fps.
    // Slow GPU → slower seeks → same smooth motion, just lower fps.
    // ───────────────────────────────────────────────────────────────────
    const LERP       = 0.14;
    const SETTLE_EPS = 0.004;
    let   isSeeking  = false;

    const advance = () => {
      if (!video.duration || isNaN(video.duration)) return;
      const diff = targetTimeRef.current - displayedRef.current;
      if (Math.abs(diff) < SETTLE_EPS) { isSeeking = false; return; }

      isSeeking = true;
      const next = displayedRef.current + diff * LERP;
      displayedRef.current = next;
      video.currentTime    = Math.max(0, Math.min(video.duration, next));
      // Next advance() is called from the 'seeked' event — never before.
    };

    const handleSeeked = () => advance();

    video.addEventListener('seeked', handleSeeked);

    const handleMouseMove = (e) => {
      const duration = video.duration;
      if (!duration || isNaN(duration)) return;

      const currentX = e.clientX;
      if (prevXRef.current === null) { prevXRef.current = currentX; return; }

      const delta = currentX - prevXRef.current;
      prevXRef.current = currentX;

      const step = Math.max(-0.25, Math.min(0.25,
        (delta / window.innerWidth) * 0.65 * duration
      ));
      targetTimeRef.current = Math.max(0, Math.min(duration, targetTimeRef.current + step));
      if (!isSeeking) advance(); // kick off chain; seeked event carries it forward
    };

    const handleMouseLeave = () => { prevXRef.current = null; };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      video.removeEventListener('loadedmetadata', handleMetadata);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, []);

  const toggleService = (service) => {
    setServices((prev) => 
      prev.includes(service) 
        ? prev.filter((s) => s !== service) 
        : [...prev, service]
    );
  };

  return (
    <div className="relative bg-white text-neutral-900 font-sans selection:bg-[#EAECE9] selection:text-[#1C2E1E] antialiased overflow-x-hidden flex flex-col lg:block lg:min-h-screen">
      
      {/* 4. Interactive Navbar */}
      <header className={`fixed top-0 inset-x-0 z-[100] transition-all duration-300 flex flex-col ${
        scrolled ? 'bg-white/90 backdrop-blur-md border-b border-[#F1F3F1] shadow-sm' : 'bg-transparent'
      }`}>
        {/* Top Contact Bar */}
        <div className={`bg-[#FAFBF9] border-b border-[#F1F3F1] md:bg-transparent md:border-transparent text-[11px] font-semibold uppercase tracking-wider text-[#5A635A] py-2 px-6 sm:px-10 lg:px-16 w-full ${scrolled ? 'hidden' : 'block'}`}>
          <div className="max-w-7xl mx-auto flex flex-row justify-center sm:justify-start items-center gap-6">
            <div className="flex items-center gap-2">
              <FaPhoneAlt size={10} className="text-[#1C2E1E]" />
              <span>+91 9164752256</span>
            </div>
            <div className="flex items-center gap-2">
              <FaEnvelope size={10} className="text-[#1C2E1E]" />
              <span>contact@cyborgsapient.com</span>
            </div>
          </div>
        </div>

        {/* Navbar Row */}
        <div className={`w-full px-5 sm:px-8 flex flex-row justify-between items-center transition-all duration-300 ${
          scrolled ? 'py-3 sm:py-4' : 'py-4 sm:py-5'
        }`}>
          {/* Logo (Left side) */}
          <Link to="/" className="flex flex-row items-center gap-3">
            <img src={Logo} alt="Cyborg Sapient" className="h-9 sm:h-11 w-auto object-contain" />
          </Link>

          {/* Desktop Nav Links (Center) */}
          <nav className="hidden md:flex flex-row items-center text-[22px] text-black font-normal">
            <Link to="/" className="hover:opacity-60 transition-opacity">home</Link>
            <span className="opacity-40">,&nbsp;</span>
            <Link to="/services" className="hover:opacity-60 transition-opacity">services</Link>
            <span className="opacity-40">,&nbsp;</span>
            <Link to="/influencer" className="hover:opacity-60 transition-opacity">influencer</Link>
            <span className="opacity-40">,&nbsp;</span>
            <Link to="/upskill" className="hover:opacity-60 transition-opacity">upskill</Link>
            <span className="opacity-40">,&nbsp;</span>
            <Link to="/blog" className="hover:opacity-60 transition-opacity">blog</Link>
          </nav>

          {/* Desktop CTA (Right) */}
          <Link 
            to="/contact" 
            className="hidden md:inline-block text-[22px] text-black underline underline-offset-2 hover:opacity-60 transition-opacity"
          >
            Get in touch
          </Link>

          {/* Hamburger button (Mobile) */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex flex-col justify-between w-6 h-[16px] z-[101] relative focus:outline-none"
            aria-label="Toggle Mobile Menu"
          >
            <span className={`w-6 h-[2px] bg-black transition-all duration-300 transform ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
            <span className={`w-6 h-[2px] bg-black transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
            <span className={`w-6 h-[2px] bg-black transition-all duration-300 transform ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`md:hidden fixed inset-0 z-[99] bg-white/95 backdrop-blur-sm transition-opacity duration-300 flex flex-col items-center justify-center gap-8 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <Link 
          to="/" 
          onClick={() => setIsMobileMenuOpen(false)} 
          className="text-3xl text-black hover:opacity-60 transition-opacity font-medium"
        >
          home
        </Link>
        <Link 
          to="/services" 
          onClick={() => setIsMobileMenuOpen(false)} 
          className="text-3xl text-black hover:opacity-60 transition-opacity font-medium"
        >
          services
        </Link>
        <Link 
          to="/influencer" 
          onClick={() => setIsMobileMenuOpen(false)} 
          className="text-3xl text-black hover:opacity-60 transition-opacity font-medium"
        >
          influencer
        </Link>
        <Link 
          to="/upskill" 
          onClick={() => setIsMobileMenuOpen(false)} 
          className="text-3xl text-black hover:opacity-60 transition-opacity font-medium"
        >
          upskill
        </Link>
        <Link 
          to="/blog" 
          onClick={() => setIsMobileMenuOpen(false)} 
          className="text-3xl text-black hover:opacity-60 transition-opacity font-medium"
        >
          blog
        </Link>
        <Link 
          to="/contact" 
          onClick={() => setIsMobileMenuOpen(false)} 
          className="text-3xl text-black underline underline-offset-4 hover:opacity-60 transition-opacity font-medium mt-4"
        >
          Get in touch
        </Link>
      </div>

      {/* Hero Section Wrapper */}
      <div className="relative w-full min-h-screen overflow-hidden bg-[#FAFBF9]">
        {/* Background Video — absolute on all screen sizes */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <video
            ref={videoRef}
            muted
            playsInline
            preload={isLowEnd.current ? 'metadata' : 'auto'}
            className="w-full h-full object-cover object-right lg:object-right-bottom"
            src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260601_110537_3a579fa0-7bbc-4d94-9d25-0e816c7840f5.mp4"
          />
          {/* White gradient mask so form text stays readable on mobile */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/85 to-white/30 lg:hidden" />
        </div>

        {/* Content Layout Container */}
        <div className="relative z-10 flex flex-col w-full min-h-screen">
          <main id="spade-hero" className="w-full max-w-7xl mx-auto px-6 py-12 flex-1 flex flex-col justify-center pt-36 lg:pt-32">
            
            {/* 6. Animated Cycling Headline */}
            <div className="w-full">
              <AnimatedHeroHeadline />
            </div>

          {/* 7. Secondary Description Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          >
            <p className="text-lg md:text-xl text-[#5A635A] leading-relaxed font-normal mb-6 max-w-2xl">
              Whether you need a high-converting digital marketing campaign, a custom SaaS product, a performance website, or an AI automation pipeline — we build it end-to-end.
            </p>
            <p className="text-sm text-[#5A635A]/70 font-normal mb-10 max-w-xl leading-relaxed">
              Tell us what you're working on and we'll get back within 24 hours with a tailored plan.
            </p>
          </motion.div>

          {/* 8. Interactive Multi-Select Service Pills */}
          <div className="max-w-2xl">
            <h2 className="text-2xl font-medium tracking-tight mb-2 text-black">
              What sort of service?
            </h2>
            <p className="opacity-85 text-[#738273] mb-8">
              Select all that apply
            </p>

            <motion.div layout className="flex flex-wrap gap-3 mb-6">
              {availableServices.map((service) => {
                const isActive = selectedServices.includes(service);
                return (
                  <motion.button
                    layout
                    key={service}
                    onClick={() => toggleService(service)}
                    whileTap={{ scale: 0.95 }}
                    className={`px-6 py-3 rounded-full text-base font-medium transition-all duration-200 flex flex-row items-center gap-1.5 ${
                      isActive 
                        ? "bg-[#1C2E1E] text-white shadow-md shadow-emerald-950/5 transform" 
                        : "bg-white text-[#1C2E1E] border border-[#F1F3F1] hover:bg-[#F1F3F1]/55"
                    }`}
                  >
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.span
                          initial={{ scale: 0, width: 0, opacity: 0 }}
                          animate={{ scale: 1, width: "auto", opacity: 1 }}
                          exit={{ scale: 0, width: 0, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          className="flex items-center justify-center"
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2.5" 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            className="lucide lucide-check"
                          >
                            <path d="M20 6 9 17l-5-5"/>
                          </svg>
                        </motion.span>
                      )}
                    </AnimatePresence>
                    {service}
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Contingent Feedback Status Banner */}
            <div className="min-h-[50px]">
              <AnimatePresence mode="wait">
                {selectedServices.length === 0 ? (
                  <motion.p
                    key="empty"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 0.5, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="italic text-xs text-[#1C2E1E]"
                  >
                    Please click to select services above.
                  </motion.p>
                ) : (
                  <motion.div
                    key="active"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-row items-center justify-between bg-[#FAFBF9] border border-[#F1F3F1] rounded-2xl px-5 py-4">
                      <span className="text-[#1C2E1E] text-sm font-medium">
                        Ready to inquire about: <span className="font-semibold">{selectedServices.join(", ")}</span>
                      </span>
                      <button className="flex items-center gap-1.5 text-[#4D6D47] font-semibold uppercase text-xs tracking-wider hover:opacity-80 transition-opacity">
                        Let's Go
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14"/>
                          <path d="m12 5 7 7-7 7"/>
                        </svg>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

        </main>
      </div>
    </div>

      <PageSEO 
        title="Top Digital Marketing Company In Ramamurthy Nagar | Digital Marketing Services In Ramamurthy Nagar – Cyborgsapient Pvt. Ltd."
        description="Looking for expert digital marketing services in Ramamurthy Nagar? Cyborgsapient Pvt. Ltd. provides result-driven SEO, SMO, PPC, and online marketing solutions for businesses."
        keywords="digital marketing company in Bangalore, SEO services in Bangalore, SEO company near me, best digital marketing agency in Bangalore, website development company Bangalore, social media marketing Bangalore, Google Ads agency Bangalore, local SEO services Bangalore, digital marketing agency near me, affordable SEO services Bangalore, SEO expert in Bangalore, PPC services Bangalore, app development company Bangalore, lead generation company Bangalore, best SEO company in Bangalore, digital marketing company in Ramamurthy Nagar, SEO services in Ramamurthy Nagar, best digital marketing agency in Ramamurthy Nagar, website development company Ramamurthy Nagar, social media marketing Ramamurthy Nagar, Google Ads agency Ramamurthy Nagar, local SEO services Ramamurthy Nagar, affordable SEO services Ramamurthy Nagar, SEO expert in Ramamurthy Nagar, PPC services Ramamurthy Nagar, app development company Ramamurthy Nagar, lead generation company Ramamurthy Nagar, best SEO company in Ramamurthy Nagar"
        url="https://cyborgsapient.com/"
      />
      <OrganizationSchema />

      {/* Influencers Carousel — right after hero, same as Homepage */}
      <PremiumInfluencer />

      {/* Narrative & Story Editorial */}
      <EditorialSection />

      {/* Capabilities / Services */}
      <ServicesSection />

      {/* The Edge / Why Choose Us */}
      <WhyChooseUsSection />

      {/* How We Work / Process / Brands / Idea CTA */}
      <HowSection />

      {/* Industry Verticals */}
      <IndustryExpertiseSection />

      {/* Client Feedback */}
      <TestimonialsSection />

      {/* Help Desk / FAQs */}
      <FAQSection />

      {/* Minimal Footer */}
      <FooterSection />

      {/* CRO Conversion Elements */}
      <PersistentCTA />
      <StickyMobileCTA />

    </div>
  );
};

export default Homepage2;
